import { getErrorMessage } from "@/helpers/handleErrorResponse";
import {
  ForgotPasswordFormData,
  LoginPayload,
  PasswordFormPayload,
  SsoTokenRequestPayload
} from "@/types/auth";
import { CustomError } from "@/utils/errors";

import axios from "axios";

import axiosInstance from "../config";

export const ssoTokenRequest = async (
  url: string,
  body: SsoTokenRequestPayload
) => {
  try {
    // Use normal axios instead of axiosInstance to avoid interceptors
    const { data } = await axios.post(url, body, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      }
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getSsoProvider = async (domain: string) => {
  try {
    const { data } = await axiosInstance.get("/v1/auth/sso/providers", {
      params: { domain }
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

const buildLoginPayload = (body: LoginPayload) => {
  const grantType = body.password ? "password" : "token_exchange";
  return {
    grant_type: grantType,
    client_id: import.meta.env.VITE_CLIENT_ID,
    ...(body.password
      ? {
          email_address: body.email,
          password: body.password
        }
      : {
          token: body.token,
          app_id: body.client_id,
          tenant_id: body.tenant_id,
          token_type: "access_token"
        })
  };
};

export const loginApi = async (body: LoginPayload) => {
  try {
    const { data } = await axiosInstance.post(
      "/v1/auth/token",
      buildLoginPayload(body)
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postActivateUser = async ({
  token,
  password
}: PasswordFormPayload) => {
  try {
    const { data } = await axiosInstance.post("/v1/activate-user", {
      token,
      password
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postResetPasword = async ({
  token,
  password
}: PasswordFormPayload) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/reset-password", {
      token,
      new_password: password
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postForgotPassword = async ({ email }: ForgotPasswordFormData) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/forgot-password", {
      email_address: email.toLowerCase()
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postVerifyToken = async (token: string) => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/verify-token", {
      token
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postLogout = async () => {
  try {
    const { data } = await axiosInstance.post("/v1/auth/logout");
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const patchChangePassword = async ({
  currentPassword,
  newPassword
}: {
  currentPassword: string;
  newPassword: string;
}) => {
  try {
    const { data } = await axiosInstance.patch("/v1/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword
    });
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAuthStatus = async () => {
  try {
    const { data } = await axiosInstance.get("/v1/auth/status");
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};
